import { useTableContext } from '@core/table/features/TableContext';
import type { SortDirection } from '@core/table/types';
import { basicSort } from '@core/table/utils';

import getTableBodyLoadingCss from './styles/getTableBodyCss';
import TableCell from './TableCell';
import TableRow from './TableRow';

export type Props<T extends Record<string, any>> = {
  rows?: Array<T>;
} & React.ComponentProps<'tbody'>;

const TableBody = <T extends Record<string, any>>(props: Props<T>) => {
  const { rows, ...rest } = props;

  const {
    pagination,
    sort,
    rowHeaderAccessor,
    columns,
    id,
    serverMode,
    status,
  } = useTableContext();

  const { page, pageSize, hidePagination } = pagination;
  const { key, direction } = sort;
  const startIndex = (page - 1) * pageSize;
  let visibleRows: Array<T> = [];

  const handleSorting = (columnId: string, direction?: SortDirection) => {
    // Custom sorting function
    const customSortFn =
      columns.find((column) => column.accessor === key)?.sortComparator ||
      basicSort;

    const sortOrder = direction === 'ascending' ? 1 : -1;
    return (row1: T, row2: T) => customSortFn(row1, row2, columnId) * sortOrder;
  };

  if (rows) {
    if (serverMode) {
      /**
       * In server mode, show all rows as is. We do not handle pagination or sorting.
       */
      visibleRows = rows;
    } else {
      /** Handle Sorting first */
      rows.sort(handleSorting(key as NonNullable<string>, direction));

      /** If pagination is hidden, display all rows */
      if (hidePagination) {
        visibleRows = rows;
      } else {
        visibleRows = rows.slice(startIndex, startIndex + pageSize);
      }
    }
  }

  return (
    <tbody
      {...rest}
      css={status === 'progressing' && getTableBodyLoadingCss}
      data-testid="tbody"
    >
      {visibleRows.map((row: any, index: number) => (
        <TableRow key={`${id}-${index}-row`}>
          {columns.map(({ accessor, justify, renderCell }) => (
            <TableCell
              key={`${id}-${accessor}-${index}-cell`}
              isHeader={rowHeaderAccessor === accessor}
              justify={justify}
            >
              {renderCell ? renderCell(row) : row[accessor]}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </tbody>
  );
};

export default TableBody;
